import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '@/components/providers/ThemeProvider';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { supabaseClient } from '@/lib/supabase';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  onPasswordReset: (newPassword: string) => Promise<void>;
  email: string | undefined;
  title: string;
  subtitle?: string;
}

export const ResetPasswordForm = ({
  onPasswordReset,
  email,
  title,
  subtitle,
}: Props): JSX.Element => {
  const { resolvedTheme } = useTheme();
  const logoSrc =
    resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg';

  const { formatMessage } = useIntl();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      ...(email !== undefined ? { email } : undefined),
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['auth', 'reset-password'],
    mutationFn: async (values: FormValues) => {
      const { error } = await supabaseClient.auth.updateUser({
        password: values.password,
      });

      if (error !== null) {
        throw error;
      }

      return values.password;
    },
    onSuccess: async password => {
      toast({
        title: 'auth.resetPassword.form.submit.success',
        variant: 'default',
      });
      await onPasswordReset(password);
    },
    onError: () => {
      toast({
        title: 'auth.resetPassword.form.submit.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <Link
          to="/"
          className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white"
        >
          <img
            className="mr-2"
            src={logoSrc}
            alt="logo"
            width={75}
            height={60}
          />
        </Link>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <div>
              <h1 className="text-xl font-semibold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                {title}
              </h1>
              {subtitle !== undefined && (
                <h2 className="mt-2 text-sm font-semibold leading-tight tracking-tight text-gray-900 md:text-lg dark:text-white">
                  {subtitle}
                </h2>
              )}
            </div>
            <Form {...form}>
              <form
                className="space-y-4 md:space-y-6"
                onSubmit={form.handleSubmit(async values => {
                  await mutateAsync(values);
                })}
              >
                {email !== undefined && (
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>
                          <FormattedMessage id="auth.resetPassword.form.email" />
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            type="text"
                            disabled
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        <FormattedMessage id="auth.resetPassword.form.password.label" />
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={formatMessage({
                              id: 'auth.resetPassword.form.password.placeholder',
                            })}
                          />
                          <Button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                          >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                  disabled={isPending}
                >
                  {isPending ? (
                    <FormattedMessage id="auth.resetPassword.form.submit.pending" />
                  ) : (
                    <FormattedMessage id="auth.resetPassword.form.submit.label" />
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};
